import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
  useRef,
  useLayoutEffect,
} from 'react';
import {
  Col,
  Container,
  Row,
} from 'reactstrap';
import axios from 'axios';
import NavBar from '../components/NavBar';
import SuggestedProfiles from '../components/feeds/sidebar/SuggestedProfiles';
import PostForm from '../components/feeds/createPost/PostForm';
import {
  _imagesURL,
  FeedPostApi,
  GetCommentsApi,
  RepostApi,
  ReserveApi,
  adminBaseURL,
  FollowApi,
} from '../constants/API';
import InfiniteScroll from 'react-infinite-scroller';
import { toast } from 'react-toastify';
import {
  FeedType,
  Language,
  PostDetailContext,
  LoginUserData,
  FeedDataRefetch,
} from '../App';
import PostCardShimmer from '../components/feeds/cards/PostCardShimmer.jsx';
import LiveFeeds from '../components/feeds/sidebar/LiveFeeds.jsx';
import FeedAdvertisement from '../components/feeds/sidebar/FeedAdvertisement.jsx';
import SuggestedCoursesEvents from '../components/feeds/sidebar/SuggestedCoursesEvents.jsx';
import FeedSponsorPost from '../components/feeds/sidebar/FeedSponsorPost.jsx';
import ProfileSuggestionSlider from '../components/feeds/sidebar/ProfileSuggestionSlider.jsx';
import {
  fetchFeedData,
  likePostApi,
  reactToPostApi,
  savePostApi,
  usersLikedPostApi,
} from '../services/feedApis.js';
import { useQuery } from 'react-query';
import { getToken } from '../shared/token.js';
import PollLikesModal from '../components/modals/PollLikesModal.jsx';
import LikeUsersModal from './LikeUsersModal.jsx';
import PostCard from './PostCard.jsx';
import ReportModal from './ReportModal.jsx';
import PostSideFilters from '../components/feeds/sidebar/PostSideFilters.jsx';
import FooterLinks from '../components/feeds/sidebar/FooterLinks.jsx';
import { useNavigate, useLocation } from 'react-router-dom';
import FeedMobileFilter from '../components/feeds/sidebar/FeedMobileFilter.jsx';
import MixpanelManager from '../constants/MixpanelManager';
import { MixpanelEvents } from '../constants/MixpanelEvents';
import { useCustomToast } from '../components/common/CustomToast';
import { useSplashAds } from '../hooks/useSplashAds';

import { calculateTotalReactions } from '../components/feeds/cards/PostFooter.jsx';
import { scrollWindowInstantly } from '../utils/scrollUtils';
import { preloadFeedImages, preloadAdImages, preloadImages } from '../utils/imageCache';

const FEED_SESSION_CACHE_PREFIX = 'medical-radar:feed-cache:v1';

// In-memory module storage surviving component unmounts during the browser session
const feedMemoryStore = {};
const feedScrollPositions = {};

const clearAllFeedSessionCache = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (
        key &&
        (key.startsWith(FEED_SESSION_CACHE_PREFIX) ||
          key.startsWith('medical-radar:feed-cache') ||
          key.includes(':scroll-position') ||
          key === 'prevFeedData' ||
          key === 'prevFeedDataDetails' ||
          key === 'feed_url')
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch (e) {
    console.warn('Could not clear feed session cache:', e);
  }
};

const clearFeedMemoryCache = () => {
  Object.keys(feedMemoryStore).forEach((k) => delete feedMemoryStore[k]);
  Object.keys(feedScrollPositions).forEach((k) => delete feedScrollPositions[k]);
};

const isPageReload = () => {
  try {
    const navEntries = performance.getEntriesByType('navigation');
    if (navEntries && navEntries.length > 0) {
      return navEntries[0].type === 'reload';
    }
    return window.performance?.navigation?.type === 1;
  } catch {
    return false;
  }
};

let hasHandledPageReload = false;
const consumeIsPageReload = () => {
  if (hasHandledPageReload) return false;
  hasHandledPageReload = true;
  return isPageReload();
};

if (isPageReload()) {
  clearAllFeedSessionCache();
  clearFeedMemoryCache();
}

// Scroll restoration is anchored to the post at the top of the viewport
// (plus its offset) rather than a raw scrollY. Any height change above that
// post (sponsor banner, post form, late images) then can't shift the feed.
const FEED_ANCHOR_ATTR = 'data-feed-anchor-id';
const FEED_ANCHOR_TOP_OFFSET = 80; // clears the floating navbar
const FEED_RESTORE_SETTLE_MS = 500;
const FEED_RESTORE_MAX_MS = 2500;
const EMPTY_FEED_SCROLL_STATE = Object.freeze({ y: 0, anchorId: null, anchorOffset: 0 });

const normalizeFeedScrollState = (value) => {
  // Legacy format stored a bare scrollY number
  if (typeof value === 'number') {
    return value > 0 ? { ...EMPTY_FEED_SCROLL_STATE, y: value } : EMPTY_FEED_SCROLL_STATE;
  }
  if (value && typeof value === 'object') {
    const y = Number(value.y);
    if (!(y > 0)) return EMPTY_FEED_SCROLL_STATE;
    return {
      y,
      anchorId: value.anchorId != null ? String(value.anchorId) : null,
      anchorOffset: Number(value.anchorOffset) || 0,
    };
  }
  return EMPTY_FEED_SCROLL_STATE;
};

const readFeedScrollState = (scrollKey) => {
  if (feedScrollPositions[scrollKey]) {
    return normalizeFeedScrollState(feedScrollPositions[scrollKey]);
  }
  try {
    const raw = sessionStorage.getItem(scrollKey);
    return raw ? normalizeFeedScrollState(JSON.parse(raw)) : EMPTY_FEED_SCROLL_STATE;
  } catch {
    return EMPTY_FEED_SCROLL_STATE;
  }
};

const writeFeedScrollState = (scrollKey, state) => {
  const normalized = normalizeFeedScrollState(state);
  feedScrollPositions[scrollKey] = normalized;
  try {
    sessionStorage.setItem(scrollKey, JSON.stringify(normalized));
  } catch (e) { }
};

const captureFeedScrollState = () => {
  const y = Math.max(0, window.scrollY || 0);
  if (y <= 0) return EMPTY_FEED_SCROLL_STATE;

  const nodes = document.querySelectorAll(`[${FEED_ANCHOR_ATTR}]`);
  for (let i = 0; i < nodes.length; i += 1) {
    const rect = nodes[i].getBoundingClientRect();
    if (rect.height > 0 && rect.bottom > FEED_ANCHOR_TOP_OFFSET) {
      return {
        y,
        anchorId: nodes[i].getAttribute(FEED_ANCHOR_ATTR),
        anchorOffset: Math.round(rect.top),
      };
    }
  }
  return { y, anchorId: null, anchorOffset: 0 };
};

const findFeedAnchorNode = (anchorId) => {
  const nodes = document.querySelectorAll(`[${FEED_ANCHOR_ATTR}]`);
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i].getAttribute(FEED_ANCHOR_ATTR) === anchorId) return nodes[i];
  }
  return null;
};

const resolveFeedScrollTarget = (state) => {
  if (!state || state.y <= 0) return 0;
  if (state.anchorId) {
    const node = findFeedAnchorNode(state.anchorId);
    if (node) {
      return Math.max(
        0,
        Math.round(window.scrollY + node.getBoundingClientRect().top - state.anchorOffset)
      );
    }
  }
  return state.y;
};

const getFeedSessionCacheKey = (userId, type) =>
  `${FEED_SESSION_CACHE_PREFIX}:${userId || 'anonymous'}:${type || 'all'}`;

const readFeedSessionCache = (cacheKey) => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
    return cached?.isReady ? cached : null;
  } catch {
    return null;
  }
};

const getFeedSnapshot = (cacheKey) => {
  if (consumeIsPageReload()) {
    clearAllFeedSessionCache();
    clearFeedMemoryCache();
    return null;
  }
  if (feedMemoryStore[cacheKey]?.isReady) {
    return feedMemoryStore[cacheKey];
  }
  return readFeedSessionCache(cacheKey);
};

const Feed = memo(() => {
  const { showToast } = useCustomToast();
  const feedTypeContext = useContext(FeedType);
  const { feedType } = feedTypeContext || { feedType: 'all' };
  const postDetailContext = useContext(PostDetailContext);
  const { updatePostDetail } = postDetailContext || {};
  const languageContext = useContext(Language);
  const { lang } = languageContext || {};
  const { splashData } = useContext(LoginUserData) || {};
  const { homeRefreshVersion, finishHomeRefresh } =
    useContext(FeedDataRefetch) || {};
  const { ads: splashAds, isReady: areSplashAdsReady } = useSplashAds();
  const navigate = useNavigate();
  const location = useLocation();
  const userData = localStorage.getItem('SignupData');
  const user = userData ? JSON.parse(userData) : null;
  const user_id = user?.data?.id;
  const feedSessionCacheKey = useMemo(
    () => getFeedSessionCacheKey(user_id, feedType),
    [user_id, feedType]
  );
  const initialFeedSnapshotRef = useRef(getFeedSnapshot(feedSessionCacheKey));
  const initialFeedSnapshot = initialFeedSnapshotRef.current;
  const initialCachedFeedType = useRef(
    initialFeedSnapshot ? feedType : null
  );
  const hasFeedSessionCache = Boolean(initialFeedSnapshot);
  const feedScrollCacheKey = `${feedSessionCacheKey}:scroll-position`;
  const [playingVideo, setPlayingVideo] = useState(-1);
  const [popularCourses, setPopularCourses] = useState(
    () => initialFeedSnapshot?.popularCourses || []
  );
  const [suggestedEvents, setSuggestedEvents] = useState(
    () => initialFeedSnapshot?.suggestedEvents || []
  );
  const [leftSideFeeds, setLeftSideFeeds] = useState(
    () => initialFeedSnapshot?.leftSideFeeds || {}
  );
  const [page, setPage] = useState(() => initialFeedSnapshot?.page || 1);
  const [data, setData] = useState(() => initialFeedSnapshot?.data || {});
  const [postData, setPostData] = useState(
    () => initialFeedSnapshot?.postData || []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [postModal, setPostModal] = useState(false);
  const [postComments, setPostComments] = useState({});
  const [postId, setPostId] = useState('');
  const [postDropDown, setPostDropDown] = useState(false);
  const [dropDownId, setDropDownId] = useState(null);
  const [likeUsersModal, setLikeUsersModal] = useState(false);
  const [likeUsers, setLikeUsers] = useState({});
  const [likeSpinner, setLikeSpinner] = useState(false);
  const [likePage, setLikePage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [suggestionsData, setSuggestionsData] = useState(
    () => initialFeedSnapshot?.suggestionsData || []
  );
  const [showPost, setShowPost] = useState(false);
  const [editWebinar, setEditWebinar] = useState(false);
  const [editPost, setEditPost] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [pollId, setPollId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [reportModal, setReportModal] = useState(false);
  const [isFeedTypeChanging, setIsFeedTypeChanging] = useState(
    () => !hasFeedSessionCache
  );
  const leftSideAds = splashAds?.home_left || [];
  const rightSideAds = splashAds?.home_right || [];
  const contest_obj = splashAds?.home_center || [];
  const adsLoading = !areSplashAdsReady;
  const [exitingPostIds, setExitingPostIds] = useState([]);
  // Track if this is the initial load and feed type changes
  const isInitialLoad = useRef(true);
  const previousFeedType = useRef(feedType);
  const isDataFromHomeApi = useRef(hasFeedSessionCache); // Track if data came from home API
  const hasFeedSessionData = useRef(hasFeedSessionCache);
  const isManualHomeRefresh = useRef(false);
  const lastHomeRefreshVersion = useRef(homeRefreshVersion);
  const feedScrollCacheKeyRef = useRef(feedScrollCacheKey);
  const latestFeedScrollState = useRef(null);
  if (latestFeedScrollState.current === null) {
    // A saved position is only meaningful when the cached posts it refers to
    // are rendered on mount; otherwise the feed opens at the top.
    latestFeedScrollState.current = hasFeedSessionCache
      ? readFeedScrollState(feedScrollCacheKey)
      : EMPTY_FEED_SCROLL_STATE;
  }
  // Blocks scroll saving until the mount-time restoration has finished
  const isRestoringScroll = useRef(true);
  const isNavigatingAway = useRef(false);
  const feedColumnRef = useRef(null);

  const authorize = {
    headers: {
      Authorization: `Bearer ${getToken()}`, // Set the Authorization header
    },
  };

  sessionStorage.setItem('feed_url', window.location.href);

  useEffect(() => {
    if (initialFeedSnapshot?.postData?.length > 0) {
      preloadFeedImages(initialFeedSnapshot.postData);
    }
    if (contest_obj?.length > 0) {
      preloadAdImages(contest_obj);
    }
  }, []);

  const formatFeedPosts = useCallback((postsList) => {
    if (!Array.isArray(postsList)) return [];

    const basePostsMap = new Map();
    postsList.forEach((p) => {
      if (p && p.id && !p.repost_user) {
        basePostsMap.set(p.id, p);
      }
    });

    return postsList.map((item) => {
      if (!item) return item;

      const isPoll = item.type === "poll" || Boolean(item.option_1);
      const originalPost = item.original_id ? basePostsMap.get(item.original_id) : null;
      const originalTime = item.original_time || originalPost?.time || item.time;

      let repostCaption = "";
      let postCaption = item.caption || "";

      if (item.repost_user) {
        if (item.repost_caption && typeof item.repost_caption === "string" && item.repost_caption.trim()) {
          repostCaption = item.repost_caption.trim();
          postCaption = isPoll ? "" : (originalPost?.caption || (item.caption !== item.repost_caption ? item.caption : ""));
        } else if (isPoll && item.caption && item.caption !== "..." && item.caption.trim()) {
          repostCaption = item.caption.trim();
          postCaption = "";
        } else {
          repostCaption = "";
          postCaption = isPoll ? "" : (originalPost?.caption || item.caption || "");
        }
      }

      const totalLikes = calculateTotalReactions(
        item.reaction_counts,
        item.likes_count ?? 0
      );

      return {
        ...item,
        time: originalTime,
        original_time: originalTime,
        caption: postCaption,
        repost_caption: repostCaption,
        original_caption: isPoll ? "" : (originalPost?.caption || postCaption),
        likes_count: totalLikes,
      };
    });
  }, []);

  const { isLoading: isLoadingFeeds, isRefetching, refetch: refetchQuery } = useQuery({
    queryKey: ['feedData', feedType],
    queryFn: () => fetchFeedData({ feedType }),
    onMutate: () => {
      if (!isManualHomeRefresh.current) {
        setLoading(true);
        setIsFeedTypeChanging(true); // Show shimmer during initial/filter fetches
      }
    },
    onSuccess: (data) => {

      if (data?.status === false && data?.action === "Unauthenticated request") {
        localStorage.clear();
        sessionStorage.clear();
        window.location.replace("/");
        return;
      }

      if (data?.status) {
        setPopularCourses(data?.left?.popular || []);
        setSuggestedEvents(data?.right?.suggested || []);
        setLeftSideFeeds(data?.left);

        // Set posts data from fetchFeedData response (Home API)
        if (data?.home?.posts?.data) {
          const incomingPosts = formatFeedPosts(data.home.posts.data);
          setPostData(incomingPosts);
          setData(data.home.posts);
          setPage(1);
          isDataFromHomeApi.current = true;
          preloadFeedImages(incomingPosts);
        }

        if (data?.right?.suggested_users) {
          preloadImages(data.right.suggested_users.map((u) => u?.image).filter(Boolean));
        }

        setSuggestionsData(data?.right?.suggested_users);
        hasFeedSessionData.current = true;
      } else {
        // A non-success payload (transient server error, duplicated request
        // after a refresh, aborted response, etc.) is NOT a session failure.
        // Real session expiry is handled above ("Unauthenticated request")
        // and by the apiAdmin 401 interceptor — never log the user out here.
        console.warn('Feed home API returned a non-success response:', data);
      }
    },
    onSettled: () => {
      isManualHomeRefresh.current = false;
      finishHomeRefresh?.();
      setLoading(false);
      setTimeout(() => setIsFeedTypeChanging(false), 300);
    },
    enabled: initialCachedFeedType.current !== feedType,
    refetchOnWindowFocus: false,
    onError: (err) => console.error('Feed fetch error:', err),
  });

  useEffect(() => {
    if (location.pathname !== '/feed') {
      isNavigatingAway.current = true;
    }
  }, [location.pathname]);

  // A second click on the active Home tab is a deliberate refresh. Keep the
  // visible feed in place and use the navigation loader instead of resetting
  // the page to post skeletons.
  useEffect(() => {
    if (lastHomeRefreshVersion.current === homeRefreshVersion) return;

    lastHomeRefreshVersion.current = homeRefreshVersion;
    isManualHomeRefresh.current = true;
    initialCachedFeedType.current = null;
    delete feedMemoryStore[feedSessionCacheKey];
    try {
      sessionStorage.removeItem(feedSessionCacheKey);
    } catch (e) { }
    latestFeedScrollState.current = EMPTY_FEED_SCROLL_STATE;
    writeFeedScrollState(feedScrollCacheKey, EMPTY_FEED_SCROLL_STATE);
    hasFeedSessionData.current = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    refetchQuery()
      .catch((err) => {
        console.warn('Manual home refresh error:', err);
      })
      .finally(() => {
        isManualHomeRefresh.current = false;
        finishHomeRefresh?.();
      });
  }, [feedSessionCacheKey, homeRefreshVersion, refetchQuery, finishHomeRefresh]);

  // Keep the current feed and its pagination available while the user opens a
  // chat, notification, or another route. The cache is scoped by user and
  // feed filter, so different accounts and filters never share results.
  useEffect(() => {
    if (!hasFeedSessionData.current && postData.length === 0) return;

    hasFeedSessionData.current = true;

    const snapshot = {
      isReady: true,
      page,
      data,
      postData,
      popularCourses,
      suggestedEvents,
      leftSideFeeds,
      suggestionsData,
    };

    feedMemoryStore[feedSessionCacheKey] = snapshot;

    try {
      sessionStorage.setItem(
        feedSessionCacheKey,
        JSON.stringify(snapshot)
      );
    } catch (error) {
      // In-memory store remains intact even if sessionStorage quota is exceeded
    }
  }, [
    data,
    feedSessionCacheKey,
    leftSideFeeds,
    page,
    popularCourses,
    postData,
    suggestedEvents,
    suggestionsData,
  ]);

  // A filter change starts a fresh scroll history for the new feed type
  useEffect(() => {
    if (feedScrollCacheKeyRef.current === feedScrollCacheKey) return;
    feedScrollCacheKeyRef.current = feedScrollCacheKey;
    latestFeedScrollState.current = EMPTY_FEED_SCROLL_STATE;
  }, [feedScrollCacheKey]);

  // Record the post at the top of the viewport while the user scrolls the feed
  useEffect(() => {
    isNavigatingAway.current = false;
    let animationFrameId = null;
    let persistTimer = null;

    const persist = () => {
      persistTimer = null;
      writeFeedScrollState(feedScrollCacheKey, latestFeedScrollState.current);
    };

    const handleFeedScroll = () => {
      if (isRestoringScroll.current || isNavigatingAway.current) return;
      if (animationFrameId) return;

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        if (isRestoringScroll.current || isNavigatingAway.current) return;
        if (window.location.pathname !== '/feed') return;

        // Ignore offsets forced by the feed column being emptied/hidden
        // (e.g. content swapped out) — those are not a user position.
        if (!feedColumnRef.current || feedColumnRef.current.offsetHeight === 0) return;

        const nextState = captureFeedScrollState();
        latestFeedScrollState.current = nextState;
        feedScrollPositions[feedScrollCacheKey] = nextState;

        if (!persistTimer) {
          persistTimer = window.setTimeout(persist, 200);
        }
      });
    };

    const handlePageHide = () => {
      if (!isRestoringScroll.current) persist();
    };

    window.addEventListener('scroll', handleFeedScroll, { passive: true });
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      isNavigatingAway.current = true;
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
      if (persistTimer) window.clearTimeout(persistTimer);
      writeFeedScrollState(feedScrollCacheKey, latestFeedScrollState.current);
      window.removeEventListener('scroll', handleFeedScroll);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [feedScrollCacheKey, location.pathname]);

  // Restore the feed position before the first paint, then keep the anchor
  // post pinned while content above it settles (sponsor banner, post form,
  // late images). Ends on user input, once layout is quiet, or after a cap.
  // The window is ALWAYS positioned here: a feed left at the top reopens at the
  // top instead of inheriting the previous page's offset.
  useLayoutEffect(() => {
    const savedState = latestFeedScrollState.current;
    let isCompleted = false;
    let frameId = null;
    let settleTimer = null;
    let maxTimer = null;
    let resizeObserver = null;
    let lastAppliedY = 0;

    const applyTarget = () => {
      const target = resolveFeedScrollTarget(savedState);
      const moved = Math.abs(window.scrollY - target) > 1;
      if (moved) scrollWindowInstantly(target);
      lastAppliedY = window.scrollY;
      return moved;
    };

    const scheduleSettle = () => {
      if (settleTimer) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(endRestoration, FEED_RESTORE_SETTLE_MS);
    };

    const reapply = () => {
      if (isCompleted) return;
      if (applyTarget()) scheduleSettle();
    };

    const tick = () => {
      frameId = null;
      if (isCompleted) return;
      reapply();
      frameId = window.requestAnimationFrame(tick);
    };

    const onUserInteract = () => endRestoration();

    // A scroll we didn't cause (scrollbar drag, native scroll anchoring)
    // means the browser/user has taken over — stop correcting.
    const onScroll = () => {
      if (Math.abs(window.scrollY - lastAppliedY) > 2) endRestoration();
    };

    const interactionEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];

    function endRestoration() {
      if (isCompleted) return;
      isCompleted = true;
      isRestoringScroll.current = false;
      if (frameId) window.cancelAnimationFrame(frameId);
      if (settleTimer) window.clearTimeout(settleTimer);
      if (maxTimer) window.clearTimeout(maxTimer);
      if (resizeObserver) resizeObserver.disconnect();
      interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, onUserInteract)
      );
      window.removeEventListener('scroll', onScroll);
    }

    isRestoringScroll.current = true;
    applyTarget();

    if (savedState.y <= 0) {
      endRestoration();
    } else {
      interactionEvents.forEach((eventName) =>
        window.addEventListener(eventName, onUserInteract, { passive: true })
      );
      window.addEventListener('scroll', onScroll, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(reapply);
        resizeObserver.observe(document.body);
      }

      frameId = window.requestAnimationFrame(tick);
      scheduleSettle();
      maxTimer = window.setTimeout(endRestoration, FEED_RESTORE_MAX_MS);
    }

    return () => {
      const wasRestoring = !isCompleted;
      endRestoration();
      // Capture the final position while the feed DOM is still attached
      // (layout cleanups run before the nodes are removed).
      if (!wasRestoring && feedColumnRef.current?.offsetHeight > 0) {
        latestFeedScrollState.current = captureFeedScrollState();
      }
      writeFeedScrollState(feedScrollCacheKeyRef.current, latestFeedScrollState.current);
    };
  }, []);

  const _fetchFeedData = useCallback(async (pageNumber = 1) => {
    // Prevent _fetchFeedData for page 1 if data is from home API
    if (isDataFromHomeApi.current && pageNumber === 1) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${FeedPostApi + feedType}?page=${pageNumber}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.data.status) {
        if (pageNumber === 1) {
          const formatted = formatFeedPosts(response.data.data?.data || []);
          setData(response.data.data);
          setPostData(formatted);
          setPage(1);
          preloadFeedImages(formatted);
        } else {
          const newPosts = formatFeedPosts(response.data.data?.data || []);
          setPostData((prev) => [...prev, ...newPosts]);
          setData(response.data.data);
          setPage(pageNumber);
          preloadFeedImages(newPosts);
        }
        // Reset the flag only for pagination (pageNumber > 1)
        if (pageNumber > 1) {
          isDataFromHomeApi.current = false;
        }
      } else {
        // toast.error(response.data.error);
      }
    } catch (error) {
      // toast.error('Error fetching feed data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [feedType, formatFeedPosts]);

  const handleNewPageData = useCallback(async () => {
    // Prevent pagination if data is not yet loaded or still fetching
    if (isLoading || isLoadingFeeds || !data?.last_page || page >= data?.last_page) {
      return;
    }

    setIsLoading(true);
    const nextPage = page + 1;

    try {
      await _fetchFeedData(nextPage); // Call _fetchFeedData for pagination
    } catch (error) {
      console.error('Error loading more data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, isLoading, isLoadingFeeds, feedType, data?.last_page, _fetchFeedData]);

  // Handle feedType changes
  useEffect(() => {
    // Check if feedType has changed (not initial load)
    if (!isInitialLoad.current && previousFeedType.current !== feedType) {
      // Reset states for new feed type
      initialCachedFeedType.current = null;
      hasFeedSessionData.current = false;
      setPage(1);
      setPostData([]);
      setData({});
      setIsLoading(false);
      setIsFeedTypeChanging(true); // Show shimmer on feedType change
      isDataFromHomeApi.current = true; // Ensure _fetchFeedData is skipped for page 1
    }

    // Update refs
    isInitialLoad.current = false;
    previousFeedType.current = feedType;
  }, [feedType]);

  const handleLikeUsers = useCallback(
    async (otherId) => {
      setLikeSpinner(true);
      setLikeUsersModal(true);
      try {
        const response = await axios.get(
          `${adminBaseURL}user/post/likes/${otherId}`,
          authorize
        );
        if (response.data.status) {
          setLikeUsers(response.data.data);
        } else {
          toast.error(response.data.error);
          setLikeUsersModal(false);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLikeSpinner(false);
      }
    },
    [user_id]
  );

  const handleNewLikeUsers = useCallback(async () => {
    try {
      const response = await usersLikedPostApi({ post_id: postId, page });
      if (response.data.status) {
        const update = likeUsers.data.concat(response.data.data.data);
        setLikeUsers((prevUsers) => ({ ...prevUsers, data: update }));
        setLikePage((prevPage) => prevPage + 1);
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      console.error(error);
    }
  }, [likePage, likeUsers, postId, user_id]);

  const indicatorStyles = useMemo(
    () => ({
      background: '#fff',
      width: 20,
      height: 4,
      display: 'inline-block',
      margin: '0 4px',
      borderRadius: '1px',
    }),
    []
  );

  const handleReserveWebinar = useCallback(
    async (post_id) => {
      try {
        const updatedData = postData.map((value) =>
          value.id === post_id
            ? { ...value, isReserve: !value.isReserve }
            : value
        );
        setPostData(updatedData);
        await axios.get(`${ReserveApi}${post_id}`, authorize);
      } catch (error) {
        console.error(error);
      }
    },
    [postData, user_id]
  );

  const handlePollAnswer = useCallback(
    async (post_id, answer) => {
      try {
        const response = await axios.post(
          `${adminBaseURL}user/post/poll/answer/submit`,
          {
            post_id,
            answer: answer + 1,
          },
          authorize
        );

        setPostData((prevData) =>
          prevData.map((post) =>
            post.id === post_id
              ? {
                ...post,
                right: answer + 1,
                percs: response.data.data,
                votes_count: post.votes_count + 1,
              }
              : post
          )
        );

        if (!response.data.status) {
          toast.error(response.data.action);
        }
        MixpanelManager.track(MixpanelEvents.pollAttempted, {
          post_id: post_id,
          user_id: user_id,
          answer_selected: answer + 1
        });
      } catch (error) {
        toast.error(error.message);
      }
    },
    [postData, user_id]
  );

  const handleLike = useCallback(
    async (post_id, reaction_type = "like") => {
      const prevDataState = postData;
      try {
        const targetPost = postData.find(
          (p) => p.original_id === post_id || p.id === post_id
        );
        const currentReaction = targetPost?.reaction || targetPost?.user_reaction;
        const wasLiked = Boolean(targetPost?.isLike || currentReaction);
        const isTogglingOff = Boolean(
          wasLiked &&
          currentReaction &&
          String(currentReaction).toLowerCase() === String(reaction_type).toLowerCase()
        );
        const nextIsLiked = !isTogglingOff;
        const nextReaction = isTogglingOff ? null : reaction_type;

        setPostData((prevData) =>
          prevData.map((post) => {
            if (post.original_id === post_id || post.id === post_id) {
              const wasPostLiked = Boolean(post.isLike || post.reaction || post.user_reaction);
              const postReaction = post.reaction || post.user_reaction;
              const isCurrentTogglingOff = Boolean(
                wasPostLiked &&
                postReaction &&
                String(postReaction).toLowerCase() === String(reaction_type).toLowerCase()
              );
              const nextPostIsLiked = !isCurrentTogglingOff;
              const nextPostReaction = isCurrentTogglingOff ? null : reaction_type;

              const currentCounts = post.reaction_counts && typeof post.reaction_counts === "object"
                ? { ...post.reaction_counts }
                : {};
              if (postReaction && currentCounts[postReaction]) {
                currentCounts[postReaction] = Math.max(
                  0,
                  Number(currentCounts[postReaction] || 0) - 1
                );
                if (currentCounts[postReaction] === 0) {
                  delete currentCounts[postReaction];
                }
              }
              if (nextPostReaction) {
                currentCounts[nextPostReaction] =
                  (Number(currentCounts[nextPostReaction]) || 0) + 1;
              }

              let updatedImages = post.like_users_images
                ? [...post.like_users_images]
                : [];
              if (nextPostIsLiked && !wasPostLiked && user?.data?.image) {
                updatedImages = [
                  user.data.image,
                  ...updatedImages.filter((img) => img !== user.data.image),
                ];
              } else if (!nextPostIsLiked && wasPostLiked && user?.data?.image) {
                updatedImages = updatedImages.filter(
                  (image) => image !== user?.data?.image
                );
              }

              const countDelta =
                nextPostIsLiked && !wasPostLiked
                  ? 1
                  : !nextPostIsLiked && wasPostLiked
                    ? -1
                    : 0;

              const totalLikesFromCounts = calculateTotalReactions(
                currentCounts,
                Math.max(0, (post.likes_count || 0) + countDelta)
              );

              const updatedPost = {
                ...post,
                isLike: nextPostIsLiked,
                reaction: nextPostReaction,
                reaction_counts: currentCounts,
                likes_count: totalLikesFromCounts,
                like_users_images: updatedImages,
              };

              if (
                postDetailContext?.postDetail &&
                (postDetailContext.postDetail.id === post_id ||
                  postDetailContext.postDetail.original_id === post_id)
              ) {
                postDetailContext.updatePostDetail({
                  ...postDetailContext.postDetail,
                  ...updatedPost,
                });
              }

              return updatedPost;
            }
            return post;
          })
        );

        await reactToPostApi({ post_id, reaction_type });
      } catch (error) {
        setPostData(prevDataState);
        toast.error(error.message);
      }
    },
    [postData, user_id, user?.data?.image, postDetailContext]
  );

  // ── Sync Feed PostData with PostDetailModal changes ──
  useEffect(() => {
    const modalPost = postDetailContext?.postDetail;
    if (!modalPost || (!modalPost.id && !modalPost.original_id)) return;
    const targetId = modalPost.original_id || modalPost.id;
    setPostData((prevData) => {
      let changed = false;
      const nextData = prevData.map((post) => {
        if (post.id === targetId || post.original_id === targetId) {
          if (
            post.isLike !== modalPost.isLike ||
            post.reaction !== modalPost.reaction ||
            post.likes_count !== modalPost.likes_count ||
            post.comments_count !== modalPost.comments_count ||
            post.isSave !== modalPost.isSave ||
            post.isShare !== modalPost.isShare
          ) {
            changed = true;
            return { ...post, ...modalPost };
          }
        }
        return post;
      });
      return changed ? nextData : prevData;
    });
  }, [postDetailContext?.postDetail]);

  const handleSave = useCallback(
    async (post_id) => {
      try {
        const updatedData = postData.map((value) =>
          value.original_id === post_id ? { ...value, isSave: !value.isSave } : value
        );
        setPostData(updatedData);
        const response = await savePostApi({ post_id });
        if (response.status) {
          // toast.success(response.action);
        }
      } catch (error) {
        toast.error(error.message);
      }
    },
    [postData, user_id]
  );

  const handleRepost = useCallback(
    async (post_id, caption) => {
      const targetPost = postData.find(
        (p) => p.id === post_id || p.original_id === post_id
      );
      if (!targetPost) return;

      const previousData = postData;
      const isQuoteRepost = Boolean(caption && typeof caption === "string" && caption.trim());
      const willBeShared = isQuoteRepost ? true : !targetPost.isShare;

      const baseSharesCount =
        Number(
          targetPost.shares_count ??
          targetPost.share_count ??
          targetPost.reposts_count ??
          0
        ) || (targetPost.repost_user?.name ? 1 : 0);

      const effectivePostId = targetPost.original_id || targetPost.id || post_id;
      const isPoll = targetPost.type === "poll" || Boolean(targetPost.option_1);
      const originalBaseCaption = targetPost.original_caption !== undefined
        ? targetPost.original_caption
        : (targetPost.repost_user ? "" : (targetPost.caption || ""));

      if (willBeShared) {
        const nextSharesCount = baseSharesCount + 1;

        // Construct the new repost post that will appear at top of feed
        const newRepostPost = {
          ...targetPost,
          id: `repost_${Date.now()}_${effectivePostId}`,
          original_id: effectivePostId,
          original_caption: originalBaseCaption,
          caption: isPoll ? "" : (isQuoteRepost ? caption.trim() : originalBaseCaption),
          repost_caption: isQuoteRepost ? caption.trim() : "",
          isShare: true,
          time: targetPost.time,
          repost_user: {
            id: user_id,
            name: splashData?.user?.name || user?.data?.name || "You",
            image: splashData?.user?.image || user?.data?.image,
            user_type: user?.data?.user_type || "doctor",
          },
          repost_user_id: user_id,
          shares_count: nextSharesCount,
        };

        // Insert new repost at the top of the feed and update existing cards
        setPostData((prev) => [
          newRepostPost,
          ...prev.map((p) =>
            p.id === post_id || p.original_id === post_id || p.id === effectivePostId || p.original_id === effectivePostId
              ? {
                ...p,
                isShare: true,
                shares_count: nextSharesCount,
              }
              : p
          ),
        ]);

        // Smooth scroll to the top of feed (after advertisement) where the new post is rendered
        setTimeout(() => {
          const anchor =
            document.getElementById("mr-first-post-anchor") ||
            document.getElementById("mr-post-form-anchor");
          if (anchor) {
            anchor.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 60);
      } else {
        const unsharedCount = Math.max(
          targetPost.repost_user?.name ? 1 : 0,
          baseSharesCount - 1
        );

        // Undo repost with smooth exit animation
        const userRepostPost = postData.find(
          (p) =>
            p.repost_user?.id === user_id &&
            (p.original_id === effectivePostId || p.id === effectivePostId || p.id === post_id)
        );

        if (userRepostPost) {
          // Trigger smooth collapse exit animation on the card
          setExitingPostIds((prev) => [...prev, userRepostPost.id]);

          // Update other cards' share state immediately
          setPostData((prev) =>
            prev.map((p) =>
              p.id === post_id || p.original_id === post_id || p.id === effectivePostId || p.original_id === effectivePostId
                ? {
                  ...p,
                  isShare: false,
                  shares_count: unsharedCount,
                }
                : p
            )
          );

          // After exit animation completes (550ms), remove smoothly from DOM
          setTimeout(() => {
            setPostData((prev) =>
              prev.filter((p) => p.id !== userRepostPost.id)
            );
            setExitingPostIds((prev) =>
              prev.filter((id) => id !== userRepostPost.id)
            );
          }, 550);
        } else {
          // Update the post card directly
          setPostData((prev) =>
            prev.map((p) =>
              p.id === post_id || p.original_id === post_id || p.id === effectivePostId || p.original_id === effectivePostId
                ? {
                  ...p,
                  isShare: false,
                  shares_count: unsharedCount,
                }
                : p
            )
          );
        }
      }

      try {
        let response;
        if (isQuoteRepost) {
          try {
            response = await axios.post(
              `${RepostApi}${effectivePostId}`,
              { caption: caption.trim() },
              authorize
            );
          } catch (postErr) {
            response = await axios.get(
              `${RepostApi}${effectivePostId}?caption=${encodeURIComponent(caption.trim())}`,
              authorize
            );
          }
        } else {
          response = await axios.get(`${RepostApi}${effectivePostId}`, authorize);
        }

        if (response.data?.status) {
          const toastMsg = willBeShared
            ? lang?.repost_success || "Reposted to your feed"
            : lang?.repost_removed || "Repost removed from your feed";
          showToast(toastMsg, "success");

          if (response.data?.data?.id && willBeShared) {
            const serverId = response.data.data.id;
            setPostData((prev) =>
              prev.map((p) =>
                typeof p.id === "string" && p.id.startsWith("repost_")
                  ? {
                    ...p,
                    id: serverId,
                    repost_caption: isQuoteRepost ? caption.trim() : "",
                  }
                  : p
              )
            );
          }
        } else {
          setPostData(previousData);
          showToast(response.data?.action || response.data?.message || "Failed to repost", "error");
        }
      } catch (error) {
        setPostData(previousData);
        showToast(error.response?.data?.message || error.message || "Failed to repost", "error");
      }
    },
    [postData, user_id, splashData, authorize, showToast, lang]
  );

  const handleFollow = useCallback(
    async (id) => {
      const updatedData = postData.map((value) =>
        value.user?.id === id
          ? {
            ...value,
            user: {
              ...value.user,
              follow: !value.user?.follow,
            },
          }
          : value
      );

      setPostData(updatedData);
      try {
        const res = await axios.get(`${FollowApi}${id}`, authorize);
        if (!res.data.status) {
          console.log(res.data.error);
        }
        const isFollowing = updatedData.find(p => p.user?.id === id)?.user?.follow;
        MixpanelManager.track(isFollowing ? MixpanelEvents.followUser : MixpanelEvents.unFollowUser, {
          followed_user_id: id,
          user_id: user_id
        });
      } catch (error) {
        console.error('Error following user:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [postData, user_id]
  );

  const handleGetComments = useCallback(
    async (post_id) => {
      setIsLoading(true);
      const id = post_id || postId;
      try {
        const response = await axios.get(`${GetCommentsApi}${id}?page=1`, authorize);
        if (response.data.status) {
          setPostComments(response?.data?.data);
        }
        setIsLoading(false);
      } catch (error) {
        console.error(error);
      }
    },
    [postId, user_id]
  );

  const handlePostReport = useCallback(
    async (reportParam, categoryParam) => {
      const category =
        (typeof reportParam === "object" ? reportParam?.category : categoryParam) || "";
      const reportText =
        (typeof reportParam === "object" ? reportParam?.report : reportParam) || "";

      if (!category && !reportText?.trim()) {
        toast.error('Please enter a report');
        return;
      }
      setIsLoading(true);
      try {
        const reportData = {
          feature_id: dropDownId,
          user_id,
          category,
          report: reportText,
          type: 'post',
        };
        const response = await axios.post(
          `${adminBaseURL}app/report`,
          reportData,
          authorize
        );
        if (response.data.status) {
          toast.success('Reported Successfully!');
          setReportModal(false);
        } else {
          toast.error(response.data.action);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsLoading(false);
      }
    },
    [dropDownId, user_id]
  );

  useEffect(() => {
    const clearSessionStorageOnReload = () => {
      clearAllFeedSessionCache();
      clearFeedMemoryCache();
    };
    window.addEventListener('beforeunload', clearSessionStorageOnReload);
    return () => {
      window.removeEventListener('beforeunload', clearSessionStorageOnReload);
    };
  }, []);

  const toggleModal = useCallback((id) => {
    setPollId(id);
    setModalOpen((prev) => !prev);
  }, []);

  const isFeedsEmpty = useMemo(
    () =>
      leftSideFeeds.live?.length === 0 && leftSideFeeds.upcoming?.length === 0,
    [leftSideFeeds]
  );

  const viewAllPurchased = (course_type, pageType, type) => {
    if (pageType === location.pathname) {
      if (type === 'courses') {
        navigate(COURSES, { state: { selectedCourseType: course_type } });
      } else if (type === 'events') {
        navigate(EVENTS + '/3');
      }
    }
  };

  const handleVideoPlay = (index) => {
    setPlayingVideo(index);
  };

  const removePollAnswer = async (id) => {
    try {
      const response = await axios.get(
        `${adminBaseURL}user/post/poll/answer/remove/${id}`,
        authorize
      );
      if (response.data.status) {
        setPostData((prevData) =>
          prevData.map((post) =>
            post.id === id
              ? {
                ...post,
                right: 0,
                votes_count: post.votes_count - 1,
              }
              : post
          )
        );
        return response.data;
      } else {
        console.error('Error removing poll answer:', response.data.action);
        return null;
      }
    } catch (error) {
      console.error('Error while removing poll answer:', error.message);
      return null;
    }
  };

  const editWebinarTab = (edit) => {
    setEditWebinar(edit);
  };

  const editPostTab = (edit) => {
    setEditPost(edit);
  };

  const handlePostSelect = (object) => {
    setSelectedPost(object);
  };

  const updatePost = (postId, newCaption) => {
    setPostData((prevData) =>
      prevData.map((post) =>
        post.id === postId ? { ...post, caption: newCaption } : post
      )
    );
  };

  const deletePost = (postId) => {
    setPostData((prevData) =>
      prevData.filter((post) => post.original_id !== postId)
    );
  };

  // Use react-query refetch for refreshing feed data
  const refetchFeedData = useCallback(() => {
    setIsFeedTypeChanging(true); // Show shimmer on refetch
    refetchQuery();
  }, [refetchQuery]);

  return (
    <>
      <Container fluid className="feed-page-theme">
        <Row>
          <Col xs={12} className="px-0">
            <NavBar showPost={showPost} setShowPost={setShowPost} />
          </Col>
        </Row>
        <Row
          className="d-flex justify-content-center custom-pt"
        >
          <Col
            xs={12} sm={12} md={12} lg={3} xl={3}
            className="d-none d-xl-flex flex-column flex-wrap align-content-end"
          >
            <div style={{ position: 'sticky', top: '5rem', width: '275px', zIndex: 1 }}>
              <PostSideFilters />
              {!isFeedsEmpty && <div style={{ marginTop: '15px' }}></div>}
              <FeedAdvertisement
                left={true}
                type="feed"
                ads={leftSideAds}
                loading={adsLoading}
                new_contest_obj={contest_obj}
              />
            </div>
          </Col>

          {/* Main Content - Keep as div to preserve InfiniteScroll behavior */}
          <div
            ref={feedColumnRef}
            className="find_scroll col-md-12 feed-center-column-main"
            style={{
              maxWidth: '690px',
              width: '100%',
              paddingBottom: '4.5rem',
            }}
          >
            <FeedMobileFilter />

            <div id="mr-post-form-anchor">
              <PostForm
                selectedPost={selectedPost}
                editWebinar={editWebinar}
                setEditWebinar={setEditWebinar}
                editPost={editPost}
                setEditPost={setEditPost}
                handlePostSelect={handlePostSelect}
                showPost={showPost}
                setShowPost={setShowPost}
                refetch={refetchFeedData}
                updatePost={updatePost}
              />
            </div>
            <FeedSponsorPost
              loading={isLoadingFeeds || isLoading || isFeedTypeChanging}
              new_contest_obj={contest_obj}
            />
            <div id="mr-first-post-anchor" style={{ scrollMarginTop: '90px' }} />
            <InfiniteScroll
              key={feedType} // Force re-render when feedType changes
              pageStart={0}
              loadMore={handleNewPageData}
              hasMore={!isLoadingFeeds && !isFeedTypeChanging && page < data?.last_page}
              loader={<PostCardShimmer count={1} />}
              useWindow={true}
              threshold={100}
              initialLoad={false}
            >
              <>
                {(loading || isLoadingFeeds || isFeedTypeChanging) && !isLoading ? (
                  <div className="mt-3">
                    <PostCardShimmer count={2} />
                  </div>
                ) : (
                  postData &&
                  postData.map((value, index) =>
                    index === 3 ? (
                      <ProfileSuggestionSlider
                        key="suggestions"
                        suggestions={suggestionsData}
                        updateSuggestions={setSuggestionsData}
                      />
                    ) : (
                      <div
                        key={value.id}
                        data-feed-anchor-id={value.id}
                        className={
                          exitingPostIds.includes(value.id)
                            ? "mr-post-card-exiting"
                            : ""
                        }
                      >
                        <PostCard
                          value={value}
                          postDropDown={postDropDown}
                          setPostDropDown={setPostDropDown}
                          setReportModal={setReportModal}
                          setDropDownId={setDropDownId}
                          dropDownId={dropDownId}
                          handleRepost={handleRepost}
                          handleSave={handleSave}
                          handleFollow={handleFollow}
                          setShowPost={setShowPost}
                          editWebinarTab={editWebinarTab}
                          editPostTab={editPostTab}
                          setEditPost={setEditPost}
                          editPost={editPost}
                          handlePostSelect={handlePostSelect}
                          updatePost={updatePost}
                          deletePost={deletePost}
                          updatePostDetail={updatePostDetail}
                          handleLikeUsers={handleLikeUsers}
                          handleLike={handleLike}
                          handleGetComments={handleGetComments}
                          setPostModal={setPostModal}
                          setPostId={setPostId}
                          handleReserveWebinar={handleReserveWebinar}
                          handlePollAnswer={handlePollAnswer}
                          toggleModal={toggleModal}
                          removePollAnswer={removePollAnswer}
                          refetch={refetchFeedData}
                          playingVideo={playingVideo}
                          handleVideoPlay={handleVideoPlay}
                          indicatorStyles={indicatorStyles}
                        />
                      </div>
                    )
                  )
                )}
              </>
            </InfiniteScroll>
          </div>

          {/* Right Sidebar - Hidden on small screens AND iPad Pro */}
          <Col xs={12} sm={12} md={12} lg={3} xl={3} className="d-none d-xl-block">
            <LiveFeeds
              leftSideFeeds={leftSideFeeds}
              isLoading={isLoadingFeeds || isFeedTypeChanging || loading || isRefetching}
              isRefetching={isRefetching}
            />
            <SuggestedProfiles
              suggestions={suggestionsData}
              updateSuggestions={setSuggestionsData}
            />
            <SuggestedCoursesEvents
              tabType="courses-and-events"
              popularCourses={popularCourses}
              suggestedEvents={suggestedEvents}
              isLoading={isLoadingFeeds || isFeedTypeChanging || loading}
              viewAllPurchased={() => viewAllPurchased('popular-courses', '/feed', 'courses')}
            />
            <div style={{ position: 'sticky', top: '5rem', zIndex: 1 }}>
              <FeedAdvertisement
                right={true}
                ads={rightSideAds}
                loading={adsLoading}
                new_contest_obj={contest_obj}
              />
              <div style={{ marginTop: rightSideAds.length === 0 ? '0px' : '14px' }}>
                <FooterLinks />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <LikeUsersModal
        isOpen={likeUsersModal}
        toggleLikesModal={() => setLikeUsersModal(!likeUsersModal)}
        likeSpinner={likeSpinner}
        likeUsers={likeUsers}
        handleNewLikeUsers={handleNewLikeUsers}
      />
      {reportModal && (
        <ReportModal
          isOpen={reportModal}
          toggle={setReportModal}
          handleReport={handlePostReport}
          isLoading={isLoading}
          heading={lang.report_post}
        />
      )}
      {modalOpen && (
        <PollLikesModal
          modal={modalOpen}
          toggleModal={toggleModal}
          pollId={pollId}
        />
      )}
    </>
  );
});

export default Feed;